import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';

// project imports
import MainCard from 'components/MainCard';

// assets
import ExportOutlined from '@ant-design/icons/ExportOutlined';

export default function ListingTable({
  columns,
  rows,
  rowKey = 'id',
  searchPlaceholder = 'Search',
  emptyMessage = 'No records found',
  showSearch = true,
  showExport = true,
  exportLabel = 'Export CSV',
  onExport,
  searchFields,
  searchText: controlledSearchText,
  onSearchTextChange,
  toolbarLeft,
  rowsPerPage = 40,
  page: controlledPageProp,
  totalCount,
  onPageChange
}) {
  const [searchText, setSearchText] = useState('');
  const [localPage, setLocalPage] = useState(1);
  const isServerPagination = typeof onPageChange === 'function';
  const isControlledSearch = typeof onSearchTextChange === 'function';
  const effectiveSearchText = isControlledSearch ? controlledSearchText || '' : searchText;

  const fieldsForSearch = useMemo(() => {
    if (Array.isArray(searchFields) && searchFields.length) return searchFields;
    return columns.map((column) => column.id);
  }, [columns, searchFields]);

  const filteredRows = useMemo(() => {
    if (isControlledSearch) return rows;
    const value = effectiveSearchText.trim().toLowerCase();
    if (!value) return rows;

    return rows.filter((row) =>
      fieldsForSearch.some((field) => {
        const rowValue = row[field];
        return String(rowValue ?? '')
          .toLowerCase()
          .includes(value);
      })
    );
  }, [effectiveSearchText, fieldsForSearch, isControlledSearch, rows]);

  const localTotalCount = filteredRows.length;
  const effectiveTotalCount =
    isServerPagination && !effectiveSearchText.trim()
      ? Number(totalCount && Number(totalCount) > 0 ? totalCount : localTotalCount)
      : localTotalCount;
  const pageCount = Math.max(1, Math.ceil(Math.max(effectiveTotalCount, 0) / rowsPerPage));
  const controlledPage = Number(controlledPageProp || 1);
  const currentPage = isServerPagination ? controlledPage : localPage;
  const safePage = Math.min(Math.max(currentPage, 1), pageCount);
  const paginatedRows = useMemo(() => {
    if (isServerPagination) return filteredRows;
    const start = (safePage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, isServerPagination, rowsPerPage, safePage]);

  const startEntry = effectiveTotalCount ? (safePage - 1) * rowsPerPage + 1 : 0;
  const endEntry = effectiveTotalCount ? Math.min(startEntry + paginatedRows.length - 1, effectiveTotalCount) : 0;

  const pageNumbers = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= pageCount; i += 1) pages.push(i);
    return pages;
  }, [pageCount]);

  useEffect(() => {
    if (!isServerPagination) setLocalPage(1);
  }, [searchText, rows]);

  useEffect(() => {
    if (!isServerPagination || !effectiveSearchText.trim()) return;
    onPageChange(1);
  }, [effectiveSearchText, isServerPagination, onPageChange]);

  return (
    <MainCard content={false}>
      <Box sx={{ p: 2.5 }}>
        {(toolbarLeft || showSearch || showExport) && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 2 }}
          >
            <Box>{toolbarLeft}</Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'flex-end', gap: 1.5 }}>
              {showSearch && (
                <TextField
                  value={effectiveSearchText}
                  onChange={(event) => {
                    if (isControlledSearch) onSearchTextChange(event.target.value);
                    else setSearchText(event.target.value);
                  }}
                  size="small"
                  placeholder={searchPlaceholder}
                  sx={{ width: { xs: '100%', sm: 260 } }}
                />
              )}
              {showExport && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ExportOutlined />}
                  onClick={onExport}
                  sx={{ whiteSpace: 'nowrap', px: 2.25, textTransform: 'none' }}
                >
                  {exportLabel}
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        <TableContainer
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflowX: 'auto',
            position: 'relative',
            '& .MuiTableCell-root': {
              borderColor: 'divider',
              py: 1.25
            }
          }}
        >
          <Table sx={{ minWidth: 'max-content' }}>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600, backgroundColor: 'grey.50' } }}>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={{
                      whiteSpace: 'nowrap',
                      ...(column.sticky === 'right' && {
                        position: 'sticky',
                        right: 0,
                        zIndex: 4,
                        backgroundColor: 'grey.50',
                        boxShadow: '-2px 0 0 0 rgba(0,0,0,0.04)'
                      }),
                      ...(column.sticky === 'left' && {
                        position: 'sticky',
                        left: 0,
                        zIndex: 3,
                        backgroundColor: 'grey.50',
                        boxShadow: '2px 0 0 0 rgba(0,0,0,0.04)'
                      })
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row, rowIndex) => (
                <TableRow key={String(row[rowKey])} hover>
                  {columns.map((column) => (
                    <TableCell
                      key={`${row[rowKey]}-${column.id}`}
                      align={column.align || 'left'}
                      sx={{
                        whiteSpace: 'nowrap',
                        ...(column.sticky === 'right' && {
                          position: 'sticky',
                          right: 0,
                          zIndex: 3,
                          backgroundColor: '#fff',
                          boxShadow: '-2px 0 0 0 rgba(0,0,0,0.04)'
                        }),
                        ...(column.sticky === 'left' && {
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          backgroundColor: '#fff',
                          boxShadow: '2px 0 0 0 rgba(0,0,0,0.04)'
                        })
                      }}
                    >
                      {column.render ? column.render(row, rowIndex) : row[column.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {!filteredRows.length && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 1.5, gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Showing {startEntry} to {endEntry} of {effectiveTotalCount} entries
          </Typography>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
            <Button
              size="small"
              disabled={safePage <= 1}
              onClick={() => {
                const nextPage = Math.max(safePage - 1, 1);
                if (isServerPagination) onPageChange(nextPage);
                else setLocalPage(nextPage);
              }}
              sx={{
                textTransform: 'none',
                color: 'text.primary',
                '&:hover': {
                  color: '#fff'
                },
                '&.Mui-disabled':
                  { color: 'text.secondary' }
              }}
            >
              Previous
            </Button>
            {pageNumbers.map((pageNumber) => (
              <Button
                key={pageNumber}
                size="small"
                variant={safePage === pageNumber ? 'contained' : 'text'}
                onClick={() => {
                  if (isServerPagination) onPageChange(pageNumber);
                  else setLocalPage(pageNumber);
                }}
                sx={{
                  minWidth: 32,
                  px: 1.25,
                  transition: 'all .5s',
                  '&:hover': {
                    color: '#fff'
                  }
                }}
              >
                {pageNumber}
              </Button>
            ))}
            <Button
              size="small"
              disabled={safePage >= pageCount}
              onClick={() => {
                const nextPage = Math.min(safePage + 1, pageCount);
                if (isServerPagination) onPageChange(nextPage);
                else setLocalPage(nextPage);
              }}
              sx={{ 
                textTransform: 'none', 
                color: 'text.primary',
                '&:hover' :{
                  color: '#fff'
                },
                '&.Mui-disabled': 
                { color: 'text.secondary' } }}
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Box>
    </MainCard>
  );
}

ListingTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.oneOf(['left', 'right', 'center', 'justify', 'inherit']),
      noWrap: PropTypes.bool,
      sticky: PropTypes.oneOf(['left', 'right']),
      render: PropTypes.func
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  rowKey: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  showSearch: PropTypes.bool,
  showExport: PropTypes.bool,
  exportLabel: PropTypes.string,
  onExport: PropTypes.func,
  searchFields: PropTypes.arrayOf(PropTypes.string),
  searchText: PropTypes.string,
  onSearchTextChange: PropTypes.func,
  toolbarLeft: PropTypes.node,
  rowsPerPage: PropTypes.number,
  page: PropTypes.number,
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func
};
